import LoadingSpinner from '@components/LoadingSpinner'

import type { RefObject } from 'react'

interface InfiniteScrollTriggerProps {
  /**
   * React ref that should be attached to the trigger element for intersection observation.
   * This ref is used by the Intersection Observer API to detect when the element becomes visible.
   */
  targetRef: RefObject<HTMLDivElement>
  /** Whether there are more pages available to load */
  hasNextPage: boolean
  /** Whether a request to fetch the next page is currently in progress */
  isFetchingNextPage: boolean
  /**
   * Custom message to display while loading more content.
   * If not provided, only the loading spinner will be shown.
   */
  loadingMessage?: string
}

/**
 * A component that serves as the trigger element for infinite scrolling functionality.
 *
 * This component renders an invisible trigger element that, when observed by an
 * Intersection Observer, initiates the loading of additional content. It displays
 * a loading indicator when content is being fetched.
 *
 * The component is designed to be placed at the bottom of a scrollable list and
 * works in conjunction with the `useInfiniteScroll` hook to provide smooth
 * infinite scrolling experiences.
 *
 * @returns The trigger element or null if no more pages are available
 *
 */
export function InfiniteScrollTrigger({
  hasNextPage,
  isFetchingNextPage,
  targetRef,
  loadingMessage,
}: InfiniteScrollTriggerProps) {
  // Don't render anything if there are no more pages to load
  if (!hasNextPage) return null

  return (
    <div ref={targetRef} className="flex items-center justify-center py-8">
      {isFetchingNextPage && (
        <div className="flex items-center gap-2 text-gray-600">
          <LoadingSpinner />
          <div>{loadingMessage}</div>
        </div>
      )}
    </div>
  )
}
